import type { Command } from "../document/commands.ts";
import type { MapDocument, TerrainLayer, WorldDocument } from "../document/types.ts";
import type { TerrainDefinition, TerrainFace } from "../terrain/types.ts";
import { tilesetHeading, type CataloguePackInfo } from "./catalogue.ts";
import { FirstStroke, type PendingTerrain } from "./first-stroke.ts";
import { drawnFaceHeight, faceHeightRange, type FaceHeightField } from "./face-height.ts";
import type { TerrainSession } from "./incremental.ts";
import { renameLayerCommand, setLayerFaceHeightCommand, setLayerTerrainCommand, type NewLayerKind } from "./panel-commands.ts";
import type { Repaint } from "./store.ts";
import { colourNamed, paintedColour, tidyTerrainLayers } from "./terrain-layer-names.ts";
import { terrainEntryKey, terrainNamer, type TerrainNamer } from "./terrain-names.ts";
import { TerrainPainter } from "./terrain-painting.ts";

/**
 * Terrain authoring (ADR-038, ADR-072): the stroke painter and each terrain layer's chosen colour, the names of
 * terrain entries and the layers that follow them, choosing a terrain and colour from the palette, a layer's
 * terrain and its Cliff height, each edit one command.
 */

/**
 * How a palette click resolved to a layer (ADR-038): kept the active layer, retargeted an empty one,
 * reused another layer that already paints the entry, or added a new one.
 */
export interface TerrainChoice {
  readonly layerId: string;
  readonly outcome: "kept" | "retargeted" | "reused" | "added";
}

/**
 * What terrain authoring needs from the store. `mapId`, `terrains` and `packs` are read while the store is
 * being made, before its document is set.
 */
export interface TerrainHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly mapId: () => string;
  readonly terrains: () => readonly TerrainDefinition[];
  readonly packs: () => readonly CataloguePackInfo[];
  readonly activeLayerId: () => string | null;
  readonly activeRaw: () => string | null;
  readonly session: (layerId: string) => TerrainSession | undefined;
  readonly faceOf: (terrainId: string) => TerrainFace | undefined;
  readonly usableTerrain: (terrain: TerrainDefinition) => TerrainDefinition;
  readonly sample: (terrainId: string, colour: number, patch: number) => { cells: Uint32Array; size: number } | null;
  readonly isLocked: (layerId: string) => boolean;
  readonly isEditable: (layerId: string) => boolean;
  readonly refusesEdits: (layerId: string) => boolean;
  readonly liveRefuses: () => boolean;
  readonly setActiveLayer: (layerId: string) => void;
  readonly groupOf: (layerId: string) => string | undefined;
  readonly groupNamed: (name: string) => string | undefined;
  readonly groupOpen: (groupId: string) => boolean;
  readonly insertLayer: (kind: NewLayerKind, name: string, terrainId: string, group: string | { readonly name: string } | undefined, colour: number) => string;
  readonly run: (command: Command) => boolean;
  readonly provisional: (make: () => void) => { readonly commands: readonly Command[]; readonly restore: () => void } | null;
  readonly addPack: (packId: string, tilesets: readonly string[]) => boolean;
  readonly notify: () => void;
  readonly emitRepaint: (repaint: Repaint) => void;
}

/**
 * What callers reach of terrain authoring, through `EditorStore.terrain`.
 */
export type TerrainSlice = Pick<TerrainAuthoring, "brushSize" | "setBrushSize" | "activeColour" | "layerColour" | "begin" | "extend" | "finish" | "sample" | "name" | "original" | "layerName" | "setName" | "choose" | "chooseEntry" | "stage" | "pendingChoice" | "clearPending" | "refused" | "addLayer" | "setLayerTerrain" | "faceHeight" | "setFaceHeight">;

/**
 * The terrain author.
 */
export class TerrainAuthoring {
  private readonly chosenColours = new Map<string, number>();
  private readonly firstStroke: FirstStroke;

  /**
   * Told when a first stroke's trial could not be made, so the host can say so; the stroke does not begin.
   */
  public refused: (() => void) | null = null;
  private namerFor: { readonly names: WorldDocument["terrainNames"]; readonly terrains: readonly TerrainDefinition[]; readonly packs: ReturnType<TerrainHost["packs"]>; readonly namer: TerrainNamer } | null = null;

  /**
   * The stroke painter.
   */
  private readonly painter: TerrainPainter;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: TerrainHost) {
    this.firstStroke = new FirstStroke(host, (terrainId, colour) => this.chooseEntry(terrainId, colour), () => this.activeColour(), () => this.refused?.());
    this.painter = new TerrainPainter({
      mapId: host.mapId,
      map: host.map,
      activeLayerId: host.activeLayerId,
      refusesEdits: host.refusesEdits,
      session: host.session,
      record: (command) => this.firstStroke.record(command),
      emitRepaint: host.emitRepaint,
      chosenColour: (layerId) => this.chosenColours.get(layerId),
    });
  }

  /**
   * Whether a terrain stroke is in progress.
   *
   * @returns True mid-stroke.
   */
  public get painting(): boolean {
    return this.painter.painting;
  }

  /**
   * Brush size in vertices (terrain) or cells (collision).
   *
   * @returns The size.
   */
  public get brushSize(): number {
    return this.painter.brushSize;
  }

  /**
   * Sets the brush size without notifying, for callers that batch their own update.
   *
   * @param size Brush size.
   */
  public set brushSize(size: number) {
    this.painter.setBrushSize(size);
  }

  /**
   * Sets the brush size and notifies subscribers.
   *
   * @param size Brush size.
   */
  public setBrushSize(size: number): void {
    this.painter.setBrushSize(size);
    this.host.notify();
  }

  /**
   * The paint colour for the active layer.
   *
   * @returns The colour.
   */
  public activeColour(): number {
    return this.painter.activeColour();
  }

  /**
   * The paint colour of a terrain layer (ADR-038).
   *
   * @param layerId Layer id.
   * @returns The colour its corners hold, else the colour chosen for it, else 1.
   */
  public layerColour(layerId: string): number {
    return this.painter.layerColour(layerId);
  }

  /**
   * Starts a stroke on the active layer.
   *
   * @param erase True to paint colour 0.
   * @returns False when there is no active unlocked terrain layer or a stroke is in progress.
   */
  public begin(erase: boolean): boolean {
    if (this.host.liveRefuses()) {
      return false;
    }
    if (!this.firstStroke.prepare(erase)) {
      this.host.notify();
      return false;
    }
    const begun = this.painter.begin(erase);
    if (!begun) {
      this.firstStroke.end();
    }
    return begun;
  }

  /**
   * Chooses a terrain of a pack the world does not list: nothing changes in the document until the first stroke,
   * which adds the tilesets the terrain needs and its layer in the same command (ADR-129 A4).
   *
   * @param choice The terrain, colour, pack and needs.
   */
  public stage(choice: PendingTerrain): void {
    this.firstStroke.stage(choice);
  }

  /**
   * The terrain chosen and waiting for its first stroke.
   *
   * @returns The choice, or null.
   */
  public get pendingChoice(): PendingTerrain | null {
    return this.firstStroke.choice;
  }

  /**
   * Lets go of a chosen terrain that was never painted with.
   */
  public clearPending(): void {
    this.firstStroke.clear();
  }

  /**
   * Paints the brush at a vertex during a stroke.
   *
   * @param vx Vertex column.
   * @param vy Vertex row.
   * @returns Cells to repaint, or null when no stroke is in progress.
   */
  public extend(vx: number, vy: number): Repaint | null {
    return this.painter.extend(vx, vy);
  }

  /**
   * Ends the stroke, recording it when it changed anything; a stroke on a locked layer is discarded.
   *
   * @returns True when a command was recorded.
   */
  public finish(): boolean {
    const recorded = this.painter.finish();
    this.firstStroke.end();
    return recorded;
  }

  /**
   * Discards a stroke in progress on a layer that is no longer editable.
   */
  public releaseRefused(): void {
    this.painter.releaseRefused();
    if (!this.painter.painting) {
      this.firstStroke.end();
    }
  }

  /**
   * A rendered sample of a terrain at a colour, for palettes and the HUD.
   *
   * @param terrainId Terrain id.
   * @param colour Wang colour.
   * @param patch Painted patch size in tiles.
   * @returns The sample cells and size in tiles, or null when the terrain is unknown.
   */
  public sample(terrainId: string, colour: number, patch: number): { cells: Uint32Array; size: number } | null {
    return this.host.sample(terrainId, colour, patch);
  }

  /**
   * The display name of a terrain entry (ADR-038 amendment decision 1), through the pack's rename table and the
   * document's names.
   *
   * @param terrainId Terrain id.
   * @param colour Wang colour index.
   * @returns The name.
   */
  public name(terrainId: string, colour: number): string {
    return this.namer().name(terrainId, colour);
  }

  /**
   * The pack's own name for a terrain entry, `<pack> · <set> / <colour>`, for hover (decision 2).
   *
   * @param terrainId Terrain id.
   * @param colour Wang colour index.
   * @returns The original name.
   */
  public original(terrainId: string, colour: number): string {
    return this.namer().original(terrainId, colour);
  }

  /**
   * The display name of what a terrain layer paints: its terrain in the colour it paints.
   *
   * @param layerId Terrain layer id.
   * @returns The name.
   * @throws Error when the layer is missing or not a terrain layer.
   */
  public layerName(layerId: string): string {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "terrain") {
      throw new Error(`layer ${layerId} is not a terrain layer of map ${this.host.mapId()}`);
    }
    return this.name(layer.terrain, this.layerColour(layerId));
  }

  /**
   * Sets the author's name for a terrain entry, or with a blank name removes it, as one command that also
   * renames every unlocked layer of the map following that entry's name (decision 4). A locked one takes the
   * name when the world next opens.
   *
   * @param terrainId Terrain id.
   * @param colour Wang colour index.
   * @param name The author's name; blank for none.
   */
  public setName(terrainId: string, colour: number, name: string): void {
    const entry = terrainEntryKey(terrainId, colour);
    const document = this.host.document();
    const before = document.terrainNames?.[entry];
    const after = name.trim() === "" ? undefined : name.trim();
    if (before === after) {
      return;
    }
    const names = Object.fromEntries(Object.entries({ ...document.terrainNames, [entry]: after }).filter((pair): pair is [string, string] => pair[1] !== undefined));
    const renamed = terrainNamer(this.host.terrains(), this.host.packs(), names).name(terrainId, colour);
    const map = this.host.map();
    const renames = map.layers
      .filter((layer) => layer.kind === "terrain" && layer.autoName === true && layer.terrain === terrainId && !this.host.isLocked(layer.id) && this.layerColour(layer.id) === colour)
      .map((layer) => renameLayerCommand(map, layer.id, renamed))
      .filter((command): command is Command => command !== null);
    this.host.run({ kind: "compound", commands: [{ kind: "setTerrainName", entry, ...(before === undefined ? {} : { before }), ...(after === undefined ? {} : { after }) }, ...renames] });
  }

  /**
   * The document as opened with every following terrain layer named after what it paints, outside the
   * history; the edited map's following layers take the colour they paint as their chosen colour.
   *
   * @param document The document as read.
   * @returns The tidied document, the same object when nothing changed.
   */
  public tidied(document: WorldDocument): WorldDocument {
    const terrains = this.host.terrains();
    const namer = terrainNamer(terrains, this.host.packs(), document.terrainNames);
    const mapId = this.host.mapId();
    let changed = false;
    const maps = document.maps.map((map) => {
      const tidy = tidyTerrainLayers(map, terrains, namer);
      if (map.id === mapId) {
        tidy.colours.forEach((colour, layerId) => this.chosenColours.set(layerId, colour));
      }
      changed ||= tidy.map !== map;
      return tidy.map;
    });
    return changed ? { ...document, maps } : document;
  }

  /**
   * Takes an unpainted following layer's chosen colour back from its name after a command renamed it.
   *
   * @param layerId Layer id.
   */
  public recoverColour(layerId: string): void {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "terrain" || layer.autoName !== true || paintedColour(layer) !== 0) {
      return;
    }
    const terrain = this.host.terrains().find((t) => t.id === layer.terrain);
    const colour = terrain === undefined ? undefined : colourNamed(layer.name, terrain, this.namer());
    if (colour !== undefined) {
      this.chosenColours.set(layerId, colour);
    }
  }

  /**
   * Takes the colour a painted terrain layer's corners hold as its chosen colour.
   *
   * @param layerId Layer id.
   */
  public keepPaintedColour(layerId: string): void {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    const colour = layer?.kind === "terrain" ? paintedColour(layer) : 0;
    if (colour !== 0) {
      this.chosenColours.set(layerId, colour);
    }
  }

  /**
   * `choose` with the outcome named, so a palette can tell the user when an existing layer was
   * reused and offer a fresh one instead.
   *
   * @param terrainId Terrain id from the compile context.
   * @param colour Wang colour of that terrain; the first colour by default.
   * @returns The layer that now paints the entry and how it was chosen.
   * @throws Error when the terrain is not in the context or lacks the colour.
   */
  public chooseEntry(terrainId: string, colour?: number): TerrainChoice {
    this.firstStroke.clear();
    const { terrain, wanted } = this.requireColour(terrainId, colour);
    const map = this.host.map();
    const active = map.layers.find((l) => l.id === this.host.activeRaw());
    if (active !== undefined && active.kind === "terrain" && !this.host.isLocked(active.id)) {
      const painted = active.corners.some((c) => c !== 0);
      if (active.terrain === terrainId && this.activeColour() === wanted) {
        return { layerId: active.id, outcome: "kept" };
      }
      if (!painted) {
        const label = this.name(terrain.id, wanted);
        const follows = active.autoName === true;
        if (active.terrain !== terrainId || (follows && active.name !== label)) {
          this.setLayerTerrain(active.id, terrainId, active.variant, follows ? label : undefined);
        }
        this.chosenColours.set(active.id, wanted);
        this.host.notify();
        return { layerId: active.id, outcome: "retargeted" };
      }
    }
    const candidates = [...map.layers].reverse().filter((l): l is TerrainLayer => l.kind === "terrain" && l.terrain === terrainId && this.layerColour(l.id) === wanted && this.host.isEditable(l.id));
    const existing = candidates.find((l) => l.corners.some((c) => c !== 0)) ?? candidates[0];
    if (existing !== undefined) {
      this.host.setActiveLayer(existing.id);
      return { layerId: existing.id, outcome: "reused" };
    }
    return { layerId: this.addLayer(terrainId, wanted), outcome: "added" };
  }

  /**
   * Chooses a terrain and wang colour to paint with (ADR-038): the active terrain layer is kept when it
   * already paints that terrain and colour, retargeted when nothing is painted on it (neither when it is
   * locked); otherwise another
   * unlocked layer that already paints the entry is activated rather than duplicated (among several, the topmost
   * with a set corner, else the topmost: ADR-097's amendment of 2026-10-12, decision 29); failing that, or when
   * the active layer is not a terrain layer, a new terrain layer named after the entry is added on top and
   * made active, so painted work is never re-resolved into another terrain or colour by a palette click. A
   * layer that follows its terrain's name (`autoName`) is renamed to the new entry in the same command,
   * whether it is retargeted or takes another colour of its set; a name the author typed is kept. A layer's
   * colour is view state until its first stroke, after which its corners carry it.
   *
   * @param terrainId Terrain id from the compile context.
   * @param colour Wang colour of that terrain; the first colour by default.
   * @returns The id of the layer that now paints with the terrain and colour.
   * @throws Error when the terrain is not in the context or lacks the colour.
   */
  public choose(terrainId: string, colour?: number): string {
    return this.chooseEntry(terrainId, colour).layerId;
  }

  /**
   * Adds a new terrain layer for an entry on top of the map, named after the entry, filed under the active
   * layer's group or a group named after the tileset (created in the same command), and made active. A
   * locked or hidden group is passed over so the new layer is never born locked or hidden (P1.6-T9); when
   * the tileset's group exists but is locked or hidden, the layer stays ungrouped.
   *
   * @param terrainId Terrain id from the compile context.
   * @param colour Wang colour of that terrain; the first colour by default.
   * @returns The new layer's id.
   * @throws Error when the terrain is not in the context or lacks the colour.
   */
  public addLayer(terrainId: string, colour?: number): string {
    this.firstStroke.clear();
    const { terrain, wanted } = this.requireColour(terrainId, colour);
    const active = this.host.map().layers.find((l) => l.id === this.host.activeRaw());
    const tilesetGroup = tilesetHeading(terrain.tileset);
    const named = this.host.groupNamed(tilesetGroup);
    const open = [active === undefined ? undefined : this.host.groupOf(active.id), named].find((g) => g !== undefined && this.host.groupOpen(g));
    const target = open ?? (named === undefined ? { name: tilesetGroup } : undefined);
    const layerId = this.host.insertLayer("terrain", "", terrain.id, target, wanted);
    this.chosenColours.set(layerId, wanted);
    this.host.notify();
    return layerId;
  }

  /**
   * Changes a terrain layer's terrain and variant, rebuilding its session.
   *
   * @param layerId Layer id.
   * @param terrainId New terrain id.
   * @param variant New variant.
   * @param name New layer name, applied in the same command when given.
   * @throws Error when the layer is not a terrain layer, the terrain is unknown, or a painted corner uses a
   *   colour the new terrain lacks.
   */
  public setLayerTerrain(layerId: string, terrainId: string, variant: string, name?: string): void {
    this.firstStroke.clear();
    const terrain = this.host.terrains().find((t) => t.id === terrainId);
    if (terrain === undefined) {
      throw new Error(`terrain ${terrainId} is not in the context`);
    }
    this.host.run(setLayerTerrainCommand(this.host.map(), layerId, this.host.usableTerrain(terrain), variant, name));
  }

  /**
   * A terrain layer's Cliff height (ADR-072 decision 6): the height the editor draws its face at and the
   * heights its terrain's face can take.
   *
   * @param layerId Layer id.
   * @returns The field's height and bounds, or null for a layer that is not a terrain layer or whose terrain
   *   declares no face the editor can use.
   */
  public faceHeight(layerId: string): FaceHeightField | null {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    const face = layer?.kind === "terrain" ? this.host.faceOf(layer.terrain) : undefined;
    if (layer?.kind !== "terrain" || face === undefined) {
      return null;
    }
    return { height: drawnFaceHeight(layer, face), ...faceHeightRange(face) };
  }

  /**
   * Sets a terrain layer's Cliff height as one command, writing absence for the terrain's own height; the
   * layer's session re-runs its face pass alone and the layer repaints. A height the layer already holds
   * records nothing.
   *
   * @param layerId Terrain layer id.
   * @param height The height.
   * @returns False when a lock refused the command.
   * @throws Error when the layer is not a terrain layer, its terrain declares no face the editor can use, or
   *   the height is not an integer from the face's rows to `FACE_HEIGHT_MAX`.
   */
  public setFaceHeight(layerId: string, height: number): boolean {
    const map = this.host.map();
    const layer = map.layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "terrain") {
      throw new Error(`layer ${layerId} is not a terrain layer, so it takes no cliff height`);
    }
    const command = setLayerFaceHeightCommand(map, layerId, this.host.faceOf(layer.terrain), height);
    return command === null ? true : this.host.run(command);
  }

  /**
   * The world's terrain namer over the document's current names, rebuilt when they change.
   *
   * @returns The namer.
   */
  private namer(): TerrainNamer {
    const names = this.host.document().terrainNames;
    const terrains = this.host.terrains();
    const packs = this.host.packs();
    if (this.namerFor === null || this.namerFor.names !== names || this.namerFor.terrains !== terrains || this.namerFor.packs !== packs) {
      this.namerFor = { names, terrains, packs, namer: terrainNamer(terrains, packs, names) };
    }
    return this.namerFor.namer;
  }

  /**
   * Resolves a terrain and one of its colours.
   *
   * @param terrainId Terrain id.
   * @param colour Colour index, or the first colour.
   * @returns The terrain and colour.
   * @throws Error when either is unknown.
   */
  private requireColour(terrainId: string, colour: number | undefined): { terrain: TerrainDefinition; wanted: number } {
    const terrain = this.host.terrains().find((t) => t.id === terrainId);
    if (terrain === undefined) {
      throw new Error(`terrain ${terrainId} is not in the compile context`);
    }
    const wanted = colour ?? terrain.colors[0]?.index ?? 1;
    if (!terrain.colors.some((c) => c.index === wanted)) {
      throw new Error(`terrain ${terrainId} has no colour ${wanted}`);
    }
    return { terrain, wanted };
  }
}
